import { gql } from '@apollo/client';
import { CLIENT_FIELDS } from './clients';
import { LOCATION_FIELDS } from './locations';
import { TEAMMATE_FIELDS } from './teammates';
import { PRICING_OVERRIDE_FIELDS } from '../fragments/pricing_overrides';
import { RESOURCE_FLEET_FIELDS } from './resources';
import { RESOURCE_RULE_MAPPING_FIELDS } from './resource_rule_mappings';
import { RESOURCE_OVERRIDE_MAPPING_FIELDS } from './resource_override_mappings';
import { PARTNERSHIP_FIELDS } from '../fragments/partnerships';
import { SERVICE_LEVEL_FIELDS } from '../fragments/service_levels';
import { SURVEY_QUESTION_FIELDS } from '../fragments/surveys';
import { TAG_FIELDS, TAG_MAPPING_FIELDS } from '../fragments/tags';
import { DOCUMENT_FIELDS } from './documents';
import { INTEGRATION_OVERRIDE_FIELDS } from '../fragments/integration_overrides';
import { PERMISSION_FIELDS } from '../fragments/permissions';
import { ORGANIZATION_FIELDS } from '../fragments/organizations';

export const USER_FIELDS = gql`
    ${ORGANIZATION_FIELDS}
    fragment UserFields on users {
        circles
        device_settings
        email
        geolocation
        geolocation_expiration
        legacy_user_id
        roles
        sso_tenant
        username
        last_modified_at
        created_at
        user_id
        organization_id
        organization {
            ...OrganizationFields
        }
    }
`;

export const CLIENT_ENRICHMENTS = gql`
    ${CLIENT_FIELDS}
    ${USER_FIELDS}
    ${LOCATION_FIELDS}
    ${SERVICE_LEVEL_FIELDS}
    ${TAG_MAPPING_FIELDS}
    ${SURVEY_QUESTION_FIELDS}
    ${TAG_FIELDS}
    ${RESOURCE_FLEET_FIELDS}
    ${RESOURCE_RULE_MAPPING_FIELDS}
    ${RESOURCE_OVERRIDE_MAPPING_FIELDS}
    ${PARTNERSHIP_FIELDS}
    ${TEAMMATE_FIELDS}
    ${DOCUMENT_FIELDS}
    ${INTEGRATION_OVERRIDE_FIELDS}
    ${PERMISSION_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    fragment ClientEnrichments on clients {
        ...ClientFields
        user {
            ...UserFields
            teammatesByLeaderId {
                ...TeammateFields
                user {
                    ...UserFields
                }
            }
        }
        pricing_overrides(where: { enabled: { _eq: true }, organization_id: { _is_null: true } }) {
            ...PricingOverrideFields
        }
        carrier_partnerships(order_by: { shipper: { business_name: asc } }) {
            ...PartnershipFields
            shipper {
                ...ClientFields
                user {
                    ...UserFields
                }
            }
            contractor {
                ...ClientFields
                user {
                    ...UserFields
                    teammatesByLeaderId {
                        ...TeammateFields
                        user {
                            ...UserFields
                        }
                    }
                }
            }
        }
        locations {
            ...LocationFields
        }
        service_levels(order_by: { service_level: asc }) {
            ...ServiceLevelFields
            tags {
                ...TagMappingFields
            }
        }
        shipper_integrations: shipper_integration_overrides {
            ...IntegrationOverrideFields
        }
        shipper_partnerships(order_by: { carrier: { business_name: asc } }) {
            ...PartnershipFields
            carrier {
                ...ClientFields
                user {
                    ...UserFields
                    teammatesByLeaderId {
                        ...TeammateFields
                        user {
                            ...UserFields
                        }
                    }
                }
            }
        }
        survey_questions {
            ...SurveyQuestionFields
        }
        tags {
            ...TagFields
        }
        available_resources {
            ...ResourceFleetFields

            # Bounded by $cutoff like resources.js QUERY_BY_CLIENT — this runs on every session bootstrap.
            rules(where: { _or: [{ rule_end: { _gte: $cutoff } }, { rule_end: { _is_null: true } }] }) {
                ...ResourceRuleMappingFields
            }

            overrides(where: { _or: [{ override_end: { _gte: $cutoff } }, { override_end: { _is_null: true } }] }) {
                ...ResourceOverrideMappingFields
            }
        }
        documents {
            ...DocumentFields
        }
        permissions {
            ...PermissionFields
        }
        email_subscriptions {
            email
            communication_id
            client_id
        }
    }
`;

export const CLIENT_BY_ID = gql`
    ${CLIENT_ENRICHMENTS}
    query ClientById($client_id: uuid!, $cutoff: timestamptz) {
        client: clients_by_pk(client_id: $client_id) {
            ...ClientEnrichments
        }
    }
`;

export const TEAMMATE_CLIENT_BY_ID = gql`
    ${CLIENT_ENRICHMENTS}
    query TeammateClientById($teammate_id: uuid!, $cutoff: timestamptz) {
        teammate: teammates_by_pk(teammate_id: $teammate_id) {
            ...TeammateFields
            userByTeamLeaderId {
                user_id
                client {
                    ...ClientEnrichments
                }
            }
        }
    }
`;

export const ADMIN_CLIENTS_BY_ORGANIZATION = gql`
    ${CLIENT_ENRICHMENTS}
    query AdminClientsByOrganization($organization_id: uuid!, $cutoff: timestamptz) {
        clients(where: { user: { organization_id: { _eq: $organization_id } } }) {
            ...ClientEnrichments
        }
    }
`;

/** The organization fields `users.organization` carries, for an org an Onward admin is simulating. */
export const ORGANIZATION_BY_ID = gql`
    ${ORGANIZATION_FIELDS}
    query OrganizationById($organization_id: uuid!) {
        organization: organizations_by_pk(organization_id: $organization_id) {
            ...OrganizationFields
        }
    }
`;

export const QUERY_BY_FIREBASE_ID = gql`
    ${USER_FIELDS}
    query UsersByFirebaseId($legacy_user_id: String) {
        users(where: { legacy_user_id: { _eq: $legacy_user_id } }) {
            ...UserFields
        }
    }
`;

export const QUERY_BY_EMAIL = gql`
    ${USER_FIELDS}
    query UserByEmail($email: String) {
        users(where: { email: { _eq: $email } }) {
            ...UserFields
        }
    }
`;

export const QUERY_ONWARD_ADMINS = gql`
    ${USER_FIELDS}
    query OnwardAdmins {
        users(where: { roles: { _has_key: "ONWARD_ADMIN" } }, order_by: { username: asc_nulls_last }) {
            ...UserFields
        }
    }
`;
